import { useState, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import {
  CloseOutlined, SlidersOutlined, ArrowRightOutlined, ArrowLeftOutlined,
  PlusOutlined, DeleteOutlined, SaveOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { PercentInput } from '../percentInput.jsx';

/* ----------------------------- field renderer ------------------------------ */
function inferUnit(field) {
  const d = (field.descriptor || '').toLowerCase();
  if (field.key === "market_size") return null;
  if (d.includes('%')) return '%';
  if (field.inputType === 'number' && /(price|salary|amount|size|cost|cash|valuation|revenue)/.test(d)) return '$';
  return null;
}

export function renderFbField(field, item, handleInputChange, editable = true, handleInputBlur) {
  const { key, descriptor, inputType, options } = field;
  const id = key + "_" + (item.id ?? "x");
  const val = item[key] === null || item[key] === undefined ? '' : item[key];
  const isPercent = !!field.isPercent;
  const unit = inferUnit(field);
  const hasPre = unit === '$' && !isPercent;
  const hasSuf = isPercent || unit === '%';
  return (
    <div className="pn-field" key={key}>
      <label htmlFor={id}>{descriptor}</label>
      {options ? (
        <select id={id} className="pn-control" value={val} disabled={!editable}
          onChange={(e) => handleInputChange(e, item, key, field)}>
          {/* keep an empty option so controlled value never desyncs */}
          {val === '' && <option value="" disabled hidden>—</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <div className={"pn-input-wrap" + (hasPre ? " has-pre" : "") + (hasSuf ? " has-suf" : "")}>
          {hasPre && <span className="pre">$</span>}
          {isPercent ? (
            /* User types/sees a whole-number %, but onChange/onBlur receive the
               0.number fraction so handlers and backend stay unchanged. */
            <PercentInput id={id} className="pn-control" value={val} disabled={!editable}
              onValueChange={(v) => handleInputChange({ target: { value: v } }, item, key, field)}
              onValueBlur={handleInputBlur ? (v) => handleInputBlur({ target: { value: v } }, item, key, field) : undefined} />
          ) : (
            <input id={id} className="pn-control" type={inputType} value={val} disabled={!editable}
              onChange={(e) => handleInputChange(e, item, key, field)}
              onBlur={handleInputBlur ? (e) => handleInputBlur(e, item, key, field) : undefined} />
          )}
          {hasSuf && <span className="suf">%</span>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- layout atoms ------------------------------ */
export function Eyebrow({ children }) {
  return <div className="pn-eyebrow"><span className="pn-dot" />{children}</div>;
}

export function PageHead({ eyebrow, title, sub, children }) {
  return (
    <div className="pn-page-head">
      <div>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="pn-page-title">{title}</h1>
        {sub && <div className="pn-page-sub">{sub}</div>}
      </div>
      {children && <div style={{ display: 'flex', gap: 10 }}>{children}</div>}
    </div>
  );
}

export function StatTile({ icon, k, v, feature }) {
  return (
    <div className={"pn-stat" + (feature ? " feature" : "")}>
      <div className="k">{icon && <span className="ic">{icon}</span>}{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}

/* ------------------------------- FineTuneRail ------------------------------ */
export function FineTuneRail({ children, label = 'Fine-tune', icon = <SlidersOutlined /> }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const cancel = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const schedule = useCallback(() => { cancel(); closeTimer.current = setTimeout(() => setOpen(false), 360); }, []);
  return (
    <div className={"pn-rail" + (open ? " open" : "")}
      onMouseEnter={() => { cancel(); setOpen(true); }} onMouseLeave={schedule}>
      <button type="button" className="pn-rail-trigger" aria-haspopup="menu" aria-expanded={open}
        title="Fine-tune model" onClick={() => setOpen((o) => !o)}>
        <span className="pn-rail-trigger-ic">{icon}</span>
        <span className="pn-rail-trigger-label">{label}</span>
      </button>
      <div className="pn-rail-items" role="menu" onClick={() => setOpen(false)}>{children}</div>
    </div>
  );
}

/* Drop-in replacement for helperFunctions.SidebarButton — same props.
   isModal={false} renders the accented action row (e.g. Calculate). */
export function FineTuneItem({ icon, itemInModal, name, onClickUpdateCalc, isModal = true, width = "70%" }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <>
      <button type="button" className={"pn-rail-btn" + (isModal ? "" : " calc")}
        title={isModal ? `Edit ${name}` : name}
        onClick={isModal ? () => setOpen(true) : onClickUpdateCalc}>
        <span className="pn-rail-ic">{icon}</span>
        <span>{name}</span>
      </button>
      {isModal && (
        <Modal title={`Edit ${name}`} open={open} onOk={close} onCancel={close} width={width} style={{ top: 20 }}>
          
          {itemInModal}
        </Modal>
      )}
    </>
  );
}

export const FbIcons = {
  ArrowRightOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined,
  SaveOutlined, ThunderboltOutlined, SlidersOutlined,
};
